import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCell({ label, value, tone = "white", testid }) {
  const tones = {
    white: "text-white",
    neon: "text-neon",
    risk: "text-risk",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-md border border-line bg-ink/60 px-3.5 py-3"
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">
        {label}
      </div>
      <div className={`mt-1.5 font-mono text-sm font-semibold ${tones[tone]}`}>
        {value}
      </div>
    </div>
  );
}

export function Terminal() {
  const [tick, setTick] = useState({ price: 24816.25, dir: 1 });

  useEffect(() => {
    const id = setInterval(() => {
      setTick((p) => {
        const delta = (Math.random() - 0.45) * 5.5;
        return { price: +(p.price + delta).toFixed(2), dir: delta >= 0 ? 1 : -1 };
      });
    }, 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="hero-terminal-mockup"
      className="relative overflow-hidden rounded-xl border border-line bg-[#070809] shadow-[0_50px_140px_-30px_rgba(0,0,0,0.95),0_0_80px_rgba(0,255,102,0.05)]"
    >
      <div className="flex h-11 items-center justify-between border-b border-line bg-charcoal px-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-line" />
          <span className="h-2.5 w-2.5 rounded-full bg-line" />
          <span className="h-2.5 w-2.5 rounded-full bg-neon/80" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
          P.E.E. OS™ — Execution Terminal
        </span>
        <span
          data-testid="terminal-demo-badge"
          className="rounded border border-neon/40 bg-neon/5 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-neon"
        >
          Demo Interface
        </span>
      </div>

      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="font-display text-3xl font-extrabold tracking-tight text-white">
              NQ
            </span>
            <span className="rounded border border-line px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-mist">
              3 MIN
            </span>
            <span className="rounded border border-neon/40 bg-neon/5 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-neon">
              Professional Mode
            </span>
          </div>
          <div
            data-testid="hero-terminal-status"
            className="flex items-center gap-2 font-mono text-sm"
          >
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon" />
            <span className="text-dim">LAST</span>
            <span className={tick.dir >= 0 ? "text-neon" : "text-risk"}>
              {fmt(tick.price)}
            </span>
            {tick.dir >= 0 ? (
              <TrendingUp size={14} className="text-neon" />
            ) : (
              <TrendingDown size={14} className="text-risk" />
            )}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-5">
          <div className="chart-grid relative h-56 overflow-hidden rounded-lg border border-line bg-ink/70 sm:h-64 lg:col-span-3">
            <div className="scan-sweep" />
            <span className="absolute left-3 top-3 font-mono text-[9px] uppercase tracking-[0.25em] text-dim">
              NQ • 3 MIN • Simulated Feed
            </span>
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 400 160"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF66" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#00FF66" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,120 L28,112 L56,126 L84,98 L112,108 L140,84 L168,94 L196,64 L224,74 L252,48 L280,60 L308,34 L336,44 L364,26 L400,16 L400,160 L0,160 Z"
                fill="url(#areaFill)"
              />
              <path
                d="M0,120 L28,112 L56,126 L84,98 L112,108 L140,84 L168,94 L196,64 L224,74 L252,48 L280,60 L308,34 L336,44 L364,26 L400,16"
                fill="none"
                stroke="#00FF66"
                strokeWidth="1.5"
              />
              <line x1="0" y1="52" x2="400" y2="52" stroke="rgba(255,255,255,0.55)" strokeDasharray="5 5" strokeWidth="0.75" />
              <line x1="0" y1="34" x2="400" y2="34" stroke="rgba(0,255,102,0.45)" strokeDasharray="5 5" strokeWidth="0.75" />
              <line x1="0" y1="16" x2="400" y2="16" stroke="rgba(0,255,102,0.8)" strokeDasharray="5 5" strokeWidth="0.75" />
              <line x1="0" y1="128" x2="400" y2="128" stroke="rgba(255,77,77,0.65)" strokeDasharray="5 5" strokeWidth="0.75" />
            </svg>
            <span className="absolute right-2 top-[13px] bg-ink px-1.5 font-mono text-[9px] text-neon">TP2 24,866.25</span>
            <span className="absolute right-2 top-[31px] bg-ink px-1.5 font-mono text-[9px] text-neon/60">TP1 24,841.25</span>
            <span className="absolute right-2 top-[49px] bg-ink px-1.5 font-mono text-[9px] text-white/80">ENTRY 24,816.25</span>
            <span className="absolute right-2 top-[125px] bg-ink px-1.5 font-mono text-[9px] text-risk">STOP 24,791.25</span>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-2">
            <StatCell label="Position" value="A1" testid="terminal-stat-position" />
            <StatCell label="Event" value="BULLISH ELEPHANT" testid="terminal-stat-event" />
            <StatCell label="Trend" value="BULLISH" tone="neon" testid="terminal-stat-trend" />
            <StatCell label="Trend Health" value="STRONG" tone="neon" testid="terminal-stat-trend-health" />
            <div
              data-testid="terminal-stat-status"
              className="rounded-md border border-neon/40 bg-neon/5 px-3.5 py-3"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">
                Status
              </div>
              <div className="mt-1.5 flex items-center gap-2 font-mono text-sm font-semibold text-neon">
                <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon" />
                VALIDATED
              </div>
            </div>
            <div
              data-testid="terminal-stat-action"
              className="glow-neon rounded-md border border-neon/60 bg-neon/10 px-3.5 py-3"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-neon/70">
                Action
              </div>
              <div className="mt-1.5 font-mono text-sm font-bold text-neon">
                HOLD FOR TP2
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-line pt-4 sm:grid-cols-4">
          <div data-testid="terminal-level-entry">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">Entry</div>
            <div className="mt-1 font-mono text-base font-semibold text-white">24,816.25</div>
          </div>
          <div data-testid="terminal-level-stop">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">Stop</div>
            <div className="mt-1 font-mono text-base font-semibold text-risk">24,791.25</div>
          </div>
          <div data-testid="terminal-level-tp1">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">TP1</div>
            <div className="mt-1 font-mono text-base font-semibold text-neon/70">24,841.25</div>
          </div>
          <div data-testid="terminal-level-tp2">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-dim">TP2</div>
            <div className="mt-1 font-mono text-base font-semibold text-neon">24,866.25</div>
          </div>
        </div>
      </div>
    </div>
  );
}
